<script setup lang="ts">
import { computed } from 'vue';
import { PAGE_SIZES } from '@/appdata/page-size';
import { useUxData } from '@/stores/ux-data';

// The page-size picker for the element browsers and the Data Management lists (spec 2026-10-16).
// The model is the picked size, and null is "Default": nothing picked, so no `limit` is sent and
// the server keeps its own page size. The caller owns the value (the session's choice lives in
// predicateStore.pageSize); this atom only offers the choices and reports the pick.
//
// A Bootstrap dropdown driven by its data API alone, as AddAction.vue does. main.ts loads Bootstrap's
// JS for the whole app, so there is no Dropdown to import, create or dispose here.
const size = defineModel<number | null>({ required: true });

const ux = useUxData();
const label = computed(() => ux.labels?.common?.pageSize ?? 'Page size');
const defaultLabel = computed(() => ux.labels?.common?.pageSizeDefault ?? 'Default');
</script>

<template>
  <div class="dropup page-size-menu">
    <button
      type="button"
      class="btn btn-primary page-size-toggle position-relative"
      data-bs-toggle="dropdown"
      aria-expanded="false"
      :title="label"
    >
      <span class="fa-light fa-table-list" aria-hidden="true"></span>
      <span class="visually-hidden">{{ label }}</span>
      <span
        v-if="size !== null"
        class="page-size-badge badge rounded-pill text-bg-light border position-absolute top-0 start-100 translate-middle"
      >{{ size }}</span>
    </button>
    <ul class="dropdown-menu dropdown-menu-end">
      <li><h6 class="dropdown-header">{{ label }}</h6></li>
      <li>
        <button
          type="button"
          class="dropdown-item"
          :class="{ active: size === null }"
          :aria-current="size === null ? 'true' : undefined"
          data-page-size="default"
          @click="size = null"
        >
          {{ defaultLabel }}
          <span class="fa-light fa-check" aria-hidden="true"></span>
        </button>
      </li>
      <li><hr class="dropdown-divider" /></li>
      <li v-for="option in PAGE_SIZES" :key="option">
        <button
          type="button"
          class="dropdown-item"
          :class="{ active: size === option }"
          :aria-current="size === option ? 'true' : undefined"
          :data-page-size="option"
          @click="size = option"
        >
          {{ option }}
          <span class="fa-light fa-check" aria-hidden="true"></span>
        </button>
      </li>
    </ul>
  </div>
</template>
